import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

/**
 * Brief boot splash that draws the "TRYB" wordmark in via SVG stroke animation,
 * then fades out. Shows for ~700ms total.
 * Persists a session flag so returning-in-session visitors don't see it again.
 */
export const BootSplash = () => {
  const [visible, setVisible] = useState(() => {
    try {
      return !sessionStorage.getItem("tryb-boot-done");
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      setVisible(false);
      try {
        sessionStorage.setItem("tryb-boot-done", "1");
      } catch {
        /* ignore */
      }
    }, 900);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="boot"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] } }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-background"
          data-testid="boot-splash"
        >
          <svg
            viewBox="0 0 240 90"
            width="220"
            className="overflow-visible"
            aria-label="TRYB"
          >
            <defs>
              <linearGradient id="boot-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="hsl(221, 100%, 55%)" />
                <stop offset="50%" stopColor="hsl(270, 100%, 60%)" />
                <stop offset="100%" stopColor="hsl(320, 100%, 55%)" />
              </linearGradient>
              <filter id="boot-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* SVG-drawn TRYB wordmark — stroke animates in like handwriting */}
            <motion.text
              x="120"
              y="60"
              textAnchor="middle"
              fontFamily='"Space Grotesk", system-ui, sans-serif'
              fontWeight={800}
              fontSize="58"
              letterSpacing="-2"
              fill="none"
              stroke="url(#boot-grad)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#boot-glow)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ pathLength: { duration: 0.7, ease: "easeInOut" }, opacity: { duration: 0.2 } }}
            >
              TRYB
            </motion.text>

            {/* Fill layer fades in right after the stroke finishes */}
            <motion.text
              x="120"
              y="60"
              textAnchor="middle"
              fontFamily='"Space Grotesk", system-ui, sans-serif'
              fontWeight={800}
              fontSize="58"
              letterSpacing="-2"
              fill="url(#boot-grad)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.3 }}
            >
              TRYB
            </motion.text>
          </svg>

          {/* subtle progress bar under the wordmark */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute bottom-[42%] h-[2px] w-40 origin-left rounded-full"
            style={{
              background:
                "linear-gradient(90deg, hsl(221,100%,55%), hsl(270,100%,60%), hsl(320,100%,55%))",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BootSplash;
